import { environment } from '@accredible-frontend-v2/envs';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { ISSUER_ACCESS_LEVEL, ISSUER_PERMISSION_KIND } from '../../entities/db-issuer.model';
import { dashboardFeatureKey, DashboardState } from './dashboard.reducer';

export const selectDashboardState = createFeatureSelector<DashboardState>(dashboardFeatureKey);

export const selectDashboardStateAction = createSelector(
  selectDashboardState,
  (state) => state.action,
);

export const selectDashboardFeatureFlags = createSelector(
  selectDashboardState,
  (state) => state.featureFlags,
);

export const selectIssuer = createSelector(selectDashboardState, (state) => state.issuer);

export const selectIsAdmin = createSelector(selectDashboardState, (state) => {
  return state.issuer.permissions?.admin;
});

export const selectDashboardIsIssuerLoaded = createSelector(
  selectDashboardState,
  (state) => state.issuerLoaded,
);

export const selectWhiteLabelDomain = createSelector(selectDashboardState, (state) => {
  let whiteLabelDomain = state.issuer.department.whitelabel_domain || environment.credentialNetUrl;

  if (state.issuer.department.whitelabel_domain) {
    // If we do have a whitelabel domain, we need to prepend it with https, always
    whiteLabelDomain = 'https://' + whiteLabelDomain;
  }

  if (whiteLabelDomain.endsWith('/')) {
    return whiteLabelDomain;
  } else {
    return whiteLabelDomain + '/';
  }
});

// To be a proper timestamp, we need to add the three 0s at the end.
// Otherwise, the value that comes from V1 always defaults to the year 1970
export const selectDepartmentCreationDate = createSelector(
  selectDashboardState,
  (state) => new Date(parseInt(state.issuer.department.created_at + '000')),
);

export const selectDepartmentPricingFeatures = createSelector(selectDashboardState, (state) => {
  return state.issuer.department.pricing.features;
});

export const selectIsIssuerInTrialPlan = createSelector(
  selectDashboardState,
  (state) => state.issuer.department.pricing.plan === 'trial',
);

export const selectSupportsDeptAdminsAndDevs = createSelector(
  selectDashboardState,
  (state) => state.issuer.department.supports_dept_admins_and_devs ?? false,
);

export const selectIssuerPermissions = createSelector(
  selectDashboardState,
  (state) => state.issuer.permissions,
);

export const selectManageDeptAdminsAndDevs = createSelector(
  selectIssuerPermissions,
  (permissions) => permissions?.manage_dept_admins_and_devs ?? false,
);

export const selectTeamPermission = createSelector(
  selectIssuerPermissions,
  (permissions) => permissions?.team ?? false,
);

export const selectSettingsPermission = createSelector(
  selectIssuerPermissions,
  (permissions) => permissions?.settings ?? false,
);

export const selectIsOrganizationAdmin = createSelector(
  selectIssuerPermissions,
  (permissions) =>
    permissions?.admin === true && permissions?.kind === ISSUER_PERMISSION_KIND.ACCOUNT_WIDE,
);

export const selectIsDepartmentAdmin = createSelector(
  selectIssuerPermissions,
  (permissions) =>
    permissions?.admin === true && permissions?.kind === ISSUER_PERMISSION_KIND.DEPARTMENT_SPECIFIC,
);

export const selectIsDeveloper = createSelector(
  selectIssuerPermissions,
  (permissions) => permissions?.access_level === ISSUER_ACCESS_LEVEL.DEVELOPER,
);

export const selectDepartmentName = createSelector(
  selectDashboardState,
  (state) => state.issuer.department.name,
);

export const selectDepartmentSkillSources = createSelector(
  selectDashboardState,
  (state) => state.issuer.department.skill_sources,
);

export const selectSkillFrameworkAccessRevoked = createSelector(
  selectDashboardState,
  (state) => state.issuer.department.skill_framework_access_revoked ?? false,
);

export const selectSkillFrameworkRevocation = createSelector(
  selectDashboardState,
  (state) => state.issuer.department.skill_framework_revocation,
);

export const selectCanAccessSkillsConfiguration = createSelector(
  selectIssuer,
  selectSkillFrameworkAccessRevoked,
  (issuer, isAccessRevoked) => issuer.department.pricing.plan === 'custom' || isAccessRevoked,
);

export const selectDashboardDepartments = createSelector(
  selectDashboardState,
  (state) => state.departments,
);

export const selectIssuerCreditsLeft = createSelector(
  selectDashboardState,
  (state) => state.issuer.department.certificate_left,
);

export const selectDashboardMFAStatus = createSelector(
  selectDashboardState,
  (state) => state.issuer.department.security_setting.mfa_enabled,
);

export const selectUserName = createSelector(
  selectDashboardState,
  (state) => state.issuer.user?.name ?? '',
);

export const selectUserEmail = createSelector(
  selectDashboardState,
  (state) => state.issuer.user?.email ?? '',
);

export const selectCurrentDepartmentId = createSelector(
  selectDashboardState,
  (state) => state.issuer?.department?.id,
);

export const selectCurrentDepartmentTotalGroups = createSelector(selectDashboardState, (state) => {
  const currentDeptId = state.issuer?.department?.id;
  const currentDept = state.departments?.find((d) => d.id === currentDeptId);
  return currentDept?.total_groups ?? 0;
});

export const selectEssentialCampaignsFullyEnabled = createSelector(
  selectDashboardState,
  (state) => state.issuer?.department?.essential_campaigns_fully_enabled,
);

export const selectDepartmentAiOptOut = createSelector(
  selectDashboardState,
  (state) => state.issuer?.department?.ai_opt_out ?? false,
);

export const selectDepartmentSettingsAccess = createSelector(selectDashboardState, (state) => ({
  issuerLoaded: state.issuerLoaded,
  featureFlags: state.featureFlags,
  issuer: state.issuer,
}));

export const selectIssuerRole = createSelector(
  selectDashboardState,
  (state) => state.issuer?.permissions?.role,
);
